import { useEffect, useState } from "react";
import axios from "axios";
import { KeyRound, Plus, Trash2, RefreshCw, Copy, Eye, EyeOff, Server } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/lib/auth";
import { API } from "@/lib/site";

function copyToClipboard(text, label = "Copied") {
  navigator.clipboard?.writeText(text).then(
    () => toast.success(`${label} copied to clipboard`),
    () => toast.error("Couldn't copy — your browser blocked it."),
  );
}

function SourceBadge({ source }) {
  const map = {
    "external-sync": "bg-indigo-50 text-indigo-700 border-indigo-200",
    "admin-ui": "bg-amber-50 text-amber-800 border-amber-200",
    portal: "bg-slate-100 text-slate-600 border-slate-200",
  };
  const label = source === "external-sync" ? "S2S synced" : source === "admin-ui" ? "Manual" : "Portal";
  return (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-semibold rounded px-1.5 py-0.5 border ${map[source] || map.portal}`}>
      {label}
    </span>
  );
}

function EnvPill({ environment }) {
  const isLive = environment === "live";
  return (
    <span
      className={`inline-flex items-center text-[10px] uppercase tracking-wider font-bold rounded px-1.5 py-0.5 ${
        isLive ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"
      }`}
    >
      {isLive ? "Production" : "Sandbox"}
    </span>
  );
}

export default function ExternalKeysPanel() {
  const { headers } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [ingestSecret, setIngestSecret] = useState(null);
  const [form, setForm] = useState({
    key: "",
    environment: "test",
    partner_email: "",
    partner_company_name: "",
    partner_contact_name: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/external-keys`, { headers: headers() });
      setKeys(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchSecret = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/ingest-secret`, { headers: headers() });
      setIngestSecret(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [keysRes, secretRes] = await Promise.all([
          axios.get(`${API}/admin/external-keys`, { headers: headers() }),
          axios.get(`${API}/admin/ingest-secret`, { headers: headers() }),
        ]);
        if (!alive) return;
        setKeys(keysRes.data);
        setIngestSecret(secretRes.data);
      } catch (err) {
        if (alive) toast.error(formatApiError(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitNewKey = async (e) => {
    e.preventDefault();
    if (!form.key.trim() || !form.partner_email.trim() || !form.partner_company_name.trim()) {
      toast.error("Key, partner email and company name are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/external-keys`, form, { headers: headers() });
      toast.success(
        data.created_partner
          ? `Created partner ${data.company_name} and registered ${data.environment} key`
          : `Registered ${data.environment} key for ${data.company_name}`,
      );
      setForm({
        key: "",
        environment: "test",
        partner_email: "",
        partner_company_name: "",
        partner_contact_name: "",
      });
      setShowAdd(false);
      fetchKeys();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const removeKey = async (k) => {
    if (!window.confirm(`Revoke ${k.environment.toUpperCase()} key for ${k.company_name}?\nThe partner won't be able to log in with this key again.`)) return;
    try {
      await axios.delete(`${API}/admin/external-keys/${k.id}`, { headers: headers() });
      toast.success(`Revoked ${k.masked}`);
      fetchKeys();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const apiBase = process.env.REACT_APP_ATLAS_API_BASE || process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const s2sCurl = ingestSecret
    ? `curl -X POST ${apiBase}${ingestSecret.sync_endpoint} \\
  -H "X-Atlas-Ingest-Secret: ${showSecret ? ingestSecret.ingest_secret : "•••••••••••••••••••••••••••"}" \\
  -H "Content-Type: application/json" \\
  -d '{"key":"afg_live_xxx","environment":"live","partner_email":"acme@bank.com","partner_company_name":"Acme Bank"}'`
    : "";

  return (
    <section className="mt-10" data-testid="admin-external-keys-panel">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="font-display text-[22px] text-[#0A1128]">External API keys</h2>
          <p className="text-[12px] text-slate-500 mt-1">
            Keys generated in your external app dashboard, registered here so partners can log into the AtlasForge portal.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchKeys}
            data-testid="ext-keys-refresh"
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white text-[#0A1128] text-[13px] font-medium px-4 py-2 hover:border-slate-400"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={() => setShowAdd((s) => !s)}
            data-testid="ext-keys-add-toggle"
            className="inline-flex items-center gap-2 rounded-full bg-[#0A1128] text-white text-[13px] font-medium px-4 py-2 hover:bg-[#1E293B]"
          >
            <Plus className="h-3.5 w-3.5" /> Register key
          </button>
        </div>
      </div>

      {showAdd && (
        <form
          onSubmit={submitNewKey}
          data-testid="ext-keys-form"
          className="rounded-2xl border border-slate-200 bg-white p-6 af-shadow-card mb-6 grid sm:grid-cols-2 gap-4"
        >
          <div className="sm:col-span-2">
            <label className="text-[12px] uppercase tracking-[0.18em] text-slate-500">API key</label>
            <input
              type="text"
              required
              value={form.key}
              onChange={(e) => setForm({ ...form, key: e.target.value })}
              placeholder="afg_test_… or afg_live_…"
              data-testid="ext-keys-input-key"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-[14px] font-mono focus:outline-none focus:ring-2 focus:ring-[#0A1128]/20 focus:border-[#0A1128]"
            />
          </div>
          <div>
            <label className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Environment</label>
            <select
              value={form.environment}
              onChange={(e) => setForm({ ...form, environment: e.target.value })}
              data-testid="ext-keys-input-env"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0A1128]/20 focus:border-[#0A1128]"
            >
              <option value="test">Sandbox / test</option>
              <option value="live">Production / live</option>
            </select>
          </div>
          <div>
            <label className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Partner email *</label>
            <input
              type="email"
              required
              value={form.partner_email}
              onChange={(e) => setForm({ ...form, partner_email: e.target.value })}
              placeholder="ops@partner-bank.com"
              data-testid="ext-keys-input-email"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0A1128]/20 focus:border-[#0A1128]"
            />
          </div>
          <div>
            <label className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Company name *</label>
            <input
              type="text"
              required
              value={form.partner_company_name}
              onChange={(e) => setForm({ ...form, partner_company_name: e.target.value })}
              placeholder="Acme Pilot Bank"
              data-testid="ext-keys-input-company"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0A1128]/20 focus:border-[#0A1128]"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Contact name (optional)</label>
            <input
              type="text"
              value={form.partner_contact_name}
              onChange={(e) => setForm({ ...form, partner_contact_name: e.target.value })}
              placeholder="River Quinn"
              data-testid="ext-keys-input-contact"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0A1128]/20 focus:border-[#0A1128]"
            />
          </div>
          <div className="sm:col-span-2 flex items-center gap-3">
            <button
              type="submit"
              disabled={submitting}
              data-testid="ext-keys-submit"
              className="inline-flex items-center gap-2 rounded-full bg-[#0A1128] text-white text-[13px] font-medium px-5 py-2 hover:bg-[#1E293B] disabled:opacity-50"
            >
              <KeyRound className="h-3.5 w-3.5" /> {submitting ? "Saving…" : "Register key"}
            </button>
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="text-[13px] font-medium text-slate-600 hover:text-slate-900"
            >
              Cancel
            </button>
            <p className="text-[12px] text-slate-500 ml-auto">
              Existing partner? Same email auto-attaches. New email auto-creates partner record.
            </p>
          </div>
        </form>
      )}

      {/* S2S integration panel */}
      {ingestSecret && (
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 af-shadow-card mb-6">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#0A1128] text-white">
                <Server className="h-4 w-4" />
              </span>
              <div>
                <p className="font-display text-[16px] text-[#0A1128]">Server-to-server key sync</p>
                <p className="text-[12px] text-slate-500">
                  Call this from your external app every time a partner generates or rotates a key.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSecret((s) => !s)}
                data-testid="ext-keys-toggle-secret"
                className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white text-[12px] font-medium px-3 py-1.5 hover:border-slate-400"
              >
                {showSecret ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                {showSecret ? "Hide" : "Reveal"}
              </button>
              <button
                onClick={() => copyToClipboard(ingestSecret.ingest_secret, "Ingest secret")}
                data-testid="ext-keys-copy-secret"
                className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white text-[12px] font-medium px-3 py-1.5 hover:border-slate-400"
              >
                <Copy className="h-3 w-3" /> Secret
              </button>
              <button
                onClick={() => copyToClipboard(s2sCurl, "Sample curl")}
                data-testid="ext-keys-copy-curl"
                className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white text-[12px] font-medium px-3 py-1.5 hover:border-slate-400"
              >
                <Copy className="h-3 w-3" /> curl
              </button>
            </div>
          </div>
          <pre className="mt-4 rounded-lg bg-[#0A1128] text-[#E0E7FF] text-[12px] leading-relaxed p-4 overflow-x-auto font-mono">
{s2sCurl}
          </pre>
          <p className="text-[12px] text-slate-500 mt-2">
            Header name: <code className="bg-slate-100 rounded px-1.5 py-0.5">{ingestSecret.header_name}</code>{" "}
            • Bulk endpoint: <code className="bg-slate-100 rounded px-1.5 py-0.5">{ingestSecret.bulk_endpoint}</code>
          </p>
        </div>
      )}

      {/* Keys table */}
      <div className="rounded-2xl border border-slate-200 bg-white af-shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]" data-testid="ext-keys-table">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="font-medium px-4 py-3">Partner</th>
                <th className="font-medium px-4 py-3">Env</th>
                <th className="font-medium px-4 py-3">Key</th>
                <th className="font-medium px-4 py-3">Source</th>
                <th className="font-medium px-4 py-3">Last rotated</th>
                <th className="font-medium px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {keys.map((k) => (
                <tr key={k.id} data-testid={`ext-key-${k.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-[#0A1128]">{k.company_name}</p>
                    <p className="text-[12px] text-slate-500">{k.email}</p>
                  </td>
                  <td className="px-4 py-3"><EnvPill environment={k.environment} /></td>
                  <td className="px-4 py-3 font-mono text-[12px] text-slate-700">{k.masked}</td>
                  <td className="px-4 py-3"><SourceBadge source={k.source} /></td>
                  <td className="px-4 py-3 text-[12px] text-slate-500">
                    {k.last_rotated_at ? new Date(k.last_rotated_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => copyToClipboard(k.key, "Key")}
                        data-testid={`ext-key-copy-${k.id}`}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-[11px] font-medium px-2 py-1 hover:border-slate-400"
                      >
                        <Copy className="h-3 w-3" /> Copy
                      </button>
                      <button
                        onClick={() => removeKey(k)}
                        data-testid={`ext-key-delete-${k.id}`}
                        className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white text-rose-700 text-[11px] font-medium px-2 py-1 hover:bg-rose-50"
                      >
                        <Trash2 className="h-3 w-3" /> Revoke
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && keys.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-slate-500 text-[14px]">
                    No external keys registered yet. Click <strong>Register key</strong> or call the S2S sync endpoint.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
